import { calculateDiscountedPrice, type ReferralLinkDraft, type TicketFormSlice } from './paymentFormUtils';

type BatchSlice = {
  _id?: string;
  name?: string;
  price?: number;
  linkOnly?: boolean;
};

type TypeSlice = {
  name?: string;
  hiddenFromPublicPage?: boolean;
  isActive?: boolean;
  batches?: BatchSlice[];
};

export type TicketLinkEditRow = {
  label?: string;
  refCode?: string;
  ticketBatchId?: string | null;
  promoTypeIndex?: number;
  hasDiscount?: boolean;
  discountPercent?: number;
  finalPrice?: number;
  baseTicketName?: string;
  baseTypeIndex?: number;
};

export type PromoTarget = { typeIndex: number; batchIndex: number };

/** Nome do ingresso público a partir de "Ingresso padrão — Nome do link" */
export function parsePromoBaseName(promoTypeName?: string): string | null {
  if (!promoTypeName?.trim()) return null;
  const sep = ' — ';
  const idx = promoTypeName.indexOf(sep);
  if (idx > 0) return promoTypeName.slice(0, idx).trim();
  return null;
}

function findPublicTypeIndexByName(ticketTypes: TypeSlice[], name: string): number | null {
  const idx = ticketTypes.findIndex(
    (type) => !type.hiddenFromPublicPage && type.isActive !== false && type.name?.trim() === name.trim()
  );
  return idx >= 0 ? idx : null;
}

function getDefaultPublicBasePrice(ticketTypes: TypeSlice[]): number | null {
  const publicTypes = ticketTypes.filter(
    (type) => !type.hiddenFromPublicPage && type.isActive !== false
  );
  if (publicTypes.length !== 1) return null;
  const price = Number(publicTypes[0].batches?.[0]?.price);
  return price > 0 ? price : null;
}

export function enrichBatchedTicketLink(
  link: TicketLinkEditRow,
  ticketTypes: TypeSlice[]
): TicketLinkEditRow {
  const target = findBatchedPromoTarget(link, ticketTypes);
  const promoType = target != null ? ticketTypes[target.typeIndex] : null;
  const promoBatch = promoType?.batches?.[target?.batchIndex ?? 0];
  const baseName = link.baseTicketName || parsePromoBaseName(promoType?.name) || undefined;
  const baseTypeIndex =
    link.baseTypeIndex ??
    (baseName != null ? findPublicTypeIndexByName(ticketTypes, baseName) ?? undefined : undefined);

  const enriched: TicketLinkEditRow = {
    ...link,
    hasDiscount: link.hasDiscount ?? batchedLinkHasDiscount(link, ticketTypes),
    baseTicketName: baseName,
    baseTypeIndex,
    promoTypeIndex: link.promoTypeIndex ?? target?.typeIndex,
    ticketBatchId: link.ticketBatchId ?? promoBatch?._id ?? link.ticketBatchId,
    finalPrice:
      link.finalPrice ??
      (promoBatch?.price != null && Number(promoBatch.price) >= 0
        ? Number(promoBatch.price)
        : undefined),
  };

  const basePrice = getBasePriceForBatchedLink(enriched, ticketTypes);
  const promoPrice = enriched.finalPrice ?? null;
  if (
    enriched.discountPercent == null &&
    basePrice != null &&
    promoPrice != null &&
    promoPrice > 0 &&
    promoPrice < basePrice
  ) {
    enriched.discountPercent = inferDiscountPercent(basePrice, promoPrice) ?? undefined;
  }

  return enriched;
}

export function findBatchedPromoTarget(
  link: TicketLinkEditRow,
  ticketTypes: TypeSlice[]
): PromoTarget | null {
  if (link.promoTypeIndex != null && ticketTypes[link.promoTypeIndex]) {
    return { typeIndex: link.promoTypeIndex, batchIndex: 0 };
  }

  if (link.ticketBatchId) {
    for (let typeIndex = 0; typeIndex < ticketTypes.length; typeIndex++) {
      const batchIndex = (ticketTypes[typeIndex].batches || []).findIndex(
        (batch) => batch._id === link.ticketBatchId
      );
      if (batchIndex >= 0) return { typeIndex, batchIndex };
    }
  }

  for (let typeIndex = 0; typeIndex < ticketTypes.length; typeIndex++) {
    const type = ticketTypes[typeIndex];
    if (!type.hiddenFromPublicPage) continue;
    const batch = type.batches?.[0];
    if (batch?.name === link.label || type.name?.includes(link.label || '')) {
      return { typeIndex, batchIndex: 0 };
    }
  }

  return null;
}

export function batchedLinkHasDiscount(link: TicketLinkEditRow, ticketTypes: TypeSlice[]): boolean {
  if (link.hasDiscount) return true;
  if (link.promoTypeIndex != null) return true;
  const target = findBatchedPromoTarget(link, ticketTypes);
  if (!target) return false;
  const type = ticketTypes[target.typeIndex];
  const batch = type.batches?.[target.batchIndex];
  return !!type.hiddenFromPublicPage || !!batch?.linkOnly;
}

export function getBasePriceForBatchedLink(
  link: TicketLinkEditRow,
  ticketTypes: TypeSlice[]
): number | null {
  if (link.baseTypeIndex != null) {
    const price = Number(ticketTypes[link.baseTypeIndex]?.batches?.[0]?.price);
    if (price > 0) return price;
  }

  if (link.baseTicketName) {
    const match = ticketTypes.find(
      (type) => !type.hiddenFromPublicPage && type.name?.trim() === link.baseTicketName?.trim()
    );
    const price = Number(match?.batches?.[0]?.price);
    if (price > 0) return price;
  }

  const target = findBatchedPromoTarget(link, ticketTypes);
  if (target) {
    const promoType = ticketTypes[target.typeIndex];
    const baseName = parsePromoBaseName(promoType?.name);
    if (baseName) {
      const match = ticketTypes.find(
        (type) => !type.hiddenFromPublicPage && type.name?.trim() === baseName
      );
      const price = Number(match?.batches?.[0]?.price);
      if (price > 0) return price;
    }
  }

  const singlePublic = getDefaultPublicBasePrice(ticketTypes);
  if (singlePublic != null) return singlePublic;

  if (link.finalPrice && link.discountPercent && link.discountPercent > 0 && link.discountPercent < 100) {
    return Math.round((link.finalPrice / (1 - link.discountPercent / 100)) * 100) / 100;
  }

  return null;
}

export function inferDiscountPercent(basePrice: number, finalPrice: number): number | null {
  if (basePrice <= 0 || finalPrice <= 0 || finalPrice >= basePrice) return null;
  return Math.round((1 - finalPrice / basePrice) * 100);
}

export function recalculatePromoPrice(basePrice: number, discountPercent: number): number {
  return calculateDiscountedPrice(basePrice, discountPercent);
}

export function findLegacyPromoTicketIndex(
  link: ReferralLinkDraft,
  tickets: TicketFormSlice[]
): number | null {
  if (link.usesHiddenTicket && link.ticketIndex != null) return link.ticketIndex;
  if (link.ticketIndex != null && tickets[link.ticketIndex]?.hiddenFromPublicPage) {
    return link.ticketIndex;
  }
  return null;
}

export function legacyLinkHasDiscount(link: ReferralLinkDraft, tickets: TicketFormSlice[]): boolean {
  if (link.hasDiscount) return true;
  return findLegacyPromoTicketIndex(link, tickets) != null;
}

export function getBasePriceForLegacyLink(
  link: ReferralLinkDraft,
  tickets: TicketFormSlice[]
): number | null {
  if (link.baseTicketIndex != null) {
    const price = Number(tickets[link.baseTicketIndex]?.price);
    if (price > 0) return price;
  }
  if (link.baseTicketName) {
    const match = tickets.find(
      (ticket) => !ticket.hiddenFromPublicPage && ticket.name?.trim() === link.baseTicketName?.trim()
    );
    const price = Number(match?.price);
    if (price > 0) return price;
  }
  if (link.finalPrice && link.discountPercent && link.discountPercent > 0 && link.discountPercent < 100) {
    return Math.round((link.finalPrice / (1 - link.discountPercent / 100)) * 100) / 100;
  }
  return null;
}
